import type * as React from "react";
import { useCallback } from "react";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { ArrowRight, Check, Sparkles, X } from "lucide-react";
import { cn } from "../lib/cn";
import { Button } from "../primitives/button";

/**
 * AiCellPopover — Univer canvas-pop-up content for cells modified by Ando.
 *
 * Rendered by Univer's ``FRange.attachPopup({ componentKey, extraProps })``
 * pipeline (see ``XlsxEditorInline.tsx``). Univer's popup layer lives in a
 * separate stacking context above the spreadsheet canvas and tracks the
 * anchored cell across scroll / zoom / sheet activation automatically —
 * we just render the UX.
 *
 * SOTA pattern (May 2026):
 *   - Header strip with orange Sparkles icon + "Modifica proposta dall'AI"
 *   - Diff line: ``valueBefore`` (struck-through) → ``valueAfter`` with
 *     ArrowRight icon between for direction
 *   - Optional rationale paragraph (italic, ink-3, max 240px) when the
 *     backing ``XlsxAIChange`` carries one
 *   - Two CTAs: ``Accetta`` (primary, orange) and ``Rifiuta`` (ghost)
 *   - Esc → no-op (Univer's popup manager owns close-on-blur semantics)
 *
 * ``extraProps`` injection: Univer passes the registered component a plain
 * props bag that includes whatever ``extraProps`` was passed to
 * ``attachPopup``. We narrow with a type-safe guard at render time so
 * the component doesn't blow up when Univer mounts it standalone (e.g.
 * during plugin hot-reload).
 */
export interface AiCellPopoverProps {
  /** Stable id of the ``XlsxAIChange`` this popover represents. */
  readonly changeId?: string;
  /** Pretty label for the cell (e.g. "Sheet1!B5"). */
  readonly cellLabel?: string;
  /** Stringified value BEFORE the AI overwrite (struck-through). */
  readonly valueBefore?: string;
  /** Stringified value the AI proposed. */
  readonly valueAfter?: string;
  /** Optional AI rationale (markdown / plain). Capped to ~240 chars in UX. */
  readonly rationale?: string;
  /** Accept callback — caller wires to ``handle.acceptCellAt(changeId)``. */
  readonly onAccept?: (changeId: string) => void;
  /** Reject callback — caller wires to ``handle.rejectCellAt(changeId)``. */
  readonly onReject?: (changeId: string) => void;
}

function fmt(value: unknown, trueLabel = "VERO", falseLabel = "FALSO"): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "string") return value;
  if (typeof value === "number") return String(value);
  if (typeof value === "boolean") return value ? trueLabel : falseLabel;
  return JSON.stringify(value);
}

export const AiCellPopover: React.FC<AiCellPopoverProps> = ({
  changeId,
  cellLabel,
  valueBefore,
  valueAfter,
  rationale,
  onAccept,
  onReject,
}) => {
  const { i18n } = useLingui();
  const trueLabel = i18n._(msg`VERO`);
  const falseLabel = i18n._(msg`FALSO`);
  const handleAccept = useCallback(() => {
    if (changeId && onAccept) onAccept(changeId);
  }, [changeId, onAccept]);
  const handleReject = useCallback(() => {
    if (changeId && onReject) onReject(changeId);
  }, [changeId, onReject]);

  return (
    <div
      role="dialog"
      aria-label={
        cellLabel
          ? i18n._(msg`Modifica AI proposta sulla cella ${cellLabel}`)
          : i18n._(msg`Modifica AI proposta`)
      }
      className={cn(
        "w-[280px] overflow-hidden rounded-lg border border-hairline bg-card",
        "shadow-[0_8px_24px_-8px_rgba(0,0,0,0.14)]",
        "ring-1 ring-ando-orange-soft/40",
      )}
    >
      {/* Header */}
      <div className="flex items-center gap-1.5 border-hairline border-b bg-ando-orange-soft/40 px-3 py-1.5">
        <Sparkles
          className="h-3 w-3 shrink-0 text-ando-orange"
          aria-hidden
          strokeWidth={2}
        />
        <span className="font-medium text-[10.5px] text-ando-orange uppercase tracking-[0.06em]">
          {i18n._(msg`Modifica AI`)}
        </span>
        {cellLabel ? (
          <span className="ml-auto truncate font-mono text-[10.5px] text-ink-3">
            {cellLabel}
          </span>
        ) : null}
      </div>

      {/* Diff */}
      <div className="px-3 py-2.5">
        <div className="flex items-center gap-2 text-[12.5px]">
          <span
            className="truncate text-ink-3 line-through decoration-[1.5px] decoration-rose-400"
            title={fmt(valueBefore, trueLabel, falseLabel)}
          >
            {fmt(valueBefore, trueLabel, falseLabel)}
          </span>
          <ArrowRight
            className="h-3 w-3 shrink-0 text-ink-3"
            aria-hidden
            strokeWidth={2}
          />
          <span
            className="truncate font-medium text-ink-1"
            title={fmt(valueAfter, trueLabel, falseLabel)}
          >
            {fmt(valueAfter, trueLabel, falseLabel)}
          </span>
        </div>

        {rationale ? (
          <p className="mt-2 max-h-[88px] overflow-y-auto whitespace-pre-wrap text-[11.5px] text-ink-3 italic leading-snug">
            {rationale.length > 240 ? `${rationale.slice(0, 240)}…` : rationale}
          </p>
        ) : null}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1.5 border-hairline border-t bg-cream/60 px-2 py-1.5">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleReject}
          disabled={!changeId || !onReject}
          className="flex-1"
        >
          <X className="h-3 w-3" aria-hidden strokeWidth={2} />
          {i18n._(msg`Rifiuta`)}
        </Button>
        <Button
          type="button"
          variant="primary"
          size="sm"
          onClick={handleAccept}
          disabled={!changeId || !onAccept}
          className="flex-1"
        >
          <Check className="h-3 w-3" aria-hidden strokeWidth={2} />
          {i18n._(msg`Accetta`)}
        </Button>
      </div>
    </div>
  );
};
AiCellPopover.displayName = "AiCellPopover";
